/**
 * Public Pages Entry Point
 *
 * This entry is for public-facing pages:
 * - / (Landing Page)
 * - /legal/* (Privacy and Terms)
 *
 * Uses shadcn/ui + Tailwind CSS for styling.
 *
 * Note: Demo mode (/demo/*) is handled by demo.html entry point
 * with Polaris Web Components for consistent UI with the embedded app.
 */

import React from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { ThemeProvider } from '../components/theme-provider'
import { PublicErrorBoundary } from '../components/PublicErrorBoundary'
import i18n from '../i18n/config'
import PublicApp from './PublicApp'

// Tailwind CSS for public pages
import '../index.css'

// Detect Shopify embedded context: if loaded in an iframe, redirect to app entry.
// The landing page should never be in an iframe — CSP frame-ancestors restricts
// to Shopify domains, so iframe === Shopify Admin full-reload to "/" instead of "/app".
if (window.self !== window.top) {
  const url = new URL(window.location.href)
  url.pathname = '/app'
  window.location.replace(url.toString())
} else {
  const root = document.getElementById('root')

  if (!root) {
    throw new Error('Root element not found')
  }

  const preferredLanguage = root.hasChildNodes() ? i18n.resolvedLanguage : undefined
  const app = (
    <React.StrictMode>
      <PublicErrorBoundary>
        <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <PublicApp preferredLanguage={preferredLanguage} />
          </BrowserRouter>
        </ThemeProvider>
      </PublicErrorBoundary>
    </React.StrictMode>
  )

  if (root.hasChildNodes()) {
    void i18n.changeLanguage('en').then(() => {
      hydrateRoot(root, app)
    })
  } else {
    createRoot(root).render(app)
  }

  const registerAgentTools = () => {
    import('../lib/webmcp-tools').then((module) => module.registerPublicWebMcpTools())
  }

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(registerAgentTools, { timeout: 2000 })
  } else {
    globalThis.setTimeout(registerAgentTools, 0)
  }
}
